import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ArticleShelf } from '../components/knowledge/ArticleShelf';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/auth';
import { useAdmin } from '../context/admin';
import { useQueryNav } from '../lib/nav';
import { ApiError, kbApi } from '../lib/api';
import type { KbCategory, KbTerm, KbTermInput } from '../lib/types';
import { SkeletonTable } from '../components/ui/Skeleton';

/**
 * The assistant's reference shelf.
 *
 * Two kinds of knowledge, kept apart because they are used differently:
 *
 *   Terms     one line each — what NPT, ROP or BHA means. A term reaches the
 *             model when a question names it.
 *   Articles  what does not fit in a line — how to read a DDR, what the NPT
 *             codes mean on this contract. An article reaches the model when a
 *             question is about it, even if the words differ.
 *
 * Everyone signed in may read; only the superadmin may change anything. The
 * server enforces that — the read-only view here is a courtesy.
 *
 * This is the general shelf: what holds for every company and every well.
 * What one company or one well knows about itself is written on that
 * company's page (pages/MyWells, the Knowledge tab), and the assistant reads
 * all three for a question about a well.
 */

type Tab = 'terms' | 'articles';

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

export function KnowledgeBase() {
  const { user } = useAuth();
  const { access } = useAdmin();
  const editable = user?.role === 'superadmin' && !access.preview;

  const { params, set } = useQueryNav();
  const tab: Tab = params.get('tab') === 'articles' ? 'articles' : 'terms';

  return (
    <div className="app">
      <Header context="wells" />

      <main className="kbpage">
        <div className="kbhead">
          <h1 className="kbtitle">Knowledge Base</h1>
          <p className="kbintro">
            What the assistant knows about the business before it reads a single report. Terms say
            what a word means; articles explain how things are done here. Neither is ever quoted as a
            well's value, numbers come from the documents. This shelf is general: it holds for every
            company and every well. What one company or one well knows about itself is written on that
            company's page, under Knowledge.
          </p>

          <div className="kbtabs" role="tablist">
            {(['terms', 'articles'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={tab === t ? 'kbtab is-active' : 'kbtab'}
                onClick={() => set({ tab: t === 'terms' ? null : t, article: null })}
              >
                {t === 'terms' ? 'Terms' : 'Articles'}
              </button>
            ))}
          </div>

          {!editable && (
            <p className="kbreadonly">Read only — only a superadmin can change the knowledge base.</p>
          )}
        </div>

        {tab === 'terms' ? (
          <Terms editable={editable} />
        ) : (
          <ArticleShelf
            owner={{ kind: 'general' }}
            hint="Pick an article to edit it, or write a new one. Good articles explain one thing: how to read a report, what a set of codes means, how the crew names things."
          />
        )}
      </main>
    </div>
  );
}

/* ----------------------------------------------------------------- terms */

const EMPTY_TERM: KbTermInput = {
  term: '',
  abbreviation: '',
  categorySlug: '',
  definition: '',
  unit: '',
  aliases: [],
};

function Terms({ editable }: { editable: boolean }) {
  const [terms, setTerms] = useState<KbTerm[] | null>(null);
  const [categories, setCategories] = useState<KbCategory[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  // null: no form open. '' : a new term. A slug: that term.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<KbTermInput>(EMPTY_TERM);
  const [aliases, setAliases] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    kbApi.categories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // A short pause after typing, so each keystroke is not its own request.
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      kbApi
        .terms(query.trim(), category)
        .then((items) => {
          if (!cancelled) setTerms(items);
        })
        .catch((err) => {
          if (!cancelled) {
            setTerms([]);
            setError(errorText(err, 'Could not load the terms.'));
          }
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, category, reloads]);

  const open = (t: KbTerm | null) => {
    setError(null);
    setConfirmDelete(false);
    setEditing(t ? t.slug : '');
    setDraft(
      t
        ? {
            term: t.term,
            abbreviation: t.abbreviation ?? '',
            categorySlug: t.categorySlug ?? '',
            definition: t.definition,
            unit: t.unit ?? '',
            aliases: t.aliases ?? [],
          }
        : EMPTY_TERM,
    );
    setAliases((t?.aliases ?? []).join(', '));
  };

  const close = () => {
    setEditing(null);
    setConfirmDelete(false);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || editing === null) return;

    setBusy(true);
    setError(null);

    const body: KbTermInput = {
      ...draft,
      aliases: aliases
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
    };

    try {
      if (editing === '') await kbApi.createTerm(body);
      else await kbApi.updateTerm(editing, body);
      close();
      setReloads((n) => n + 1);
    } catch (err) {
      setError(errorText(err, 'Could not save the term.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing || busy) return;

    setBusy(true);
    try {
      await kbApi.deleteTerm(editing);
      close();
      setReloads((n) => n + 1);
    } catch (err) {
      setError(errorText(err, 'Could not delete the term.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="kbpanel">
      <div className="kbtools">
        <input
          className="kbsearch"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search terms, abbreviations, definitions…"
          aria-label="Search terms"
        />
        <select
          className="kbselect"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="kbcount">{terms ? `${terms.length} terms` : ''}</span>
        {editable && (
          <button type="button" className="kbbtn kbbtn--primary" onClick={() => open(null)}>
            + New term
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert--page" role="alert">
          {error}
        </div>
      )}

      {editing !== null && (
        <form className="kbform" onSubmit={save}>
          <h2 className="kbform-title">{editing === '' ? 'New term' : `Edit ${draft.term}`}</h2>

          <div className="kbgrid">
            <label>
              Term
              <input
                value={draft.term}
                onChange={(e) => setDraft({ ...draft, term: e.target.value })}
                placeholder="Non-Productive Time"
                required
                autoFocus
              />
            </label>
            <label>
              Abbreviation
              <input
                value={draft.abbreviation}
                onChange={(e) => setDraft({ ...draft, abbreviation: e.target.value })}
                placeholder="NPT"
              />
            </label>
            <label>
              Category
              <select
                value={draft.categorySlug}
                onChange={(e) => setDraft({ ...draft, categorySlug: e.target.value })}
              >
                <option value="">—</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Unit
              <input
                value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                placeholder="hr"
              />
            </label>
          </div>

          <label>
            Definition
            <textarea
              value={draft.definition}
              onChange={(e) => setDraft({ ...draft, definition: e.target.value })}
              rows={3}
              required
              placeholder="Time lost to failures, waiting or repairs, counted in hours."
            />
          </label>

          <label>
            Other names <small>(comma separated — the assistant matches these too)</small>
            <input
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="downtime, lost time"
            />
          </label>

          <div className="kbform-actions">
            <button type="submit" className="kbbtn kbbtn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="kbbtn" onClick={close}>
              Cancel
            </button>

            <span className="kbspacer" />

            {editing !== '' &&
              (confirmDelete ? (
                <>
                  <span className="kbconfirm">Delete this term?</span>
                  <button type="button" className="kbbtn kbbtn--danger" onClick={() => void remove()} disabled={busy}>
                    Delete
                  </button>
                  <button type="button" className="kbbtn" onClick={() => setConfirmDelete(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="kbbtn kbbtn--danger" onClick={() => setConfirmDelete(true)}>
                  Delete…
                </button>
              ))}
          </div>
        </form>
      )}

      {terms === null && <SkeletonTable rows={8} cols={4} label="Loading terms" />}
      {terms?.length === 0 && <p className="content-loading">No terms match.</p>}

      {terms && terms.length > 0 && (
        <table className="kbtable">
          <thead>
            <tr>
              <th>Term</th>
              <th>Abbr.</th>
              <th>Category</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((t) => (
              <tr
                key={t.slug}
                className={editable ? 'is-clickable' : undefined}
                onClick={editable ? () => open(t) : undefined}
              >
                <td className="kbterm">{t.term}</td>
                <td>{t.abbreviation ? <code>{t.abbreviation}</code> : '—'}</td>
                <td className="kbmuted">{t.categoryName ?? '—'}</td>
                <td>
                  {t.definition}
                  {t.unit && <span className="kbmuted"> ({t.unit})</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
